import React, { useState, useEffect, useMemo } from 'react';
import { db, auth } from '../../../../firebase';
import { collection, query, where, doc, setDoc, getDoc, getDocs, updateDoc, onSnapshot, writeBatch, arrayUnion, arrayRemove, deleteField } from 'firebase/firestore';
import { COLLECTIONS } from '../../../../models/collectionNames';
import { 
    KoreanTextModel, 
    BookQuestionSetModel, 
    BookSetModel, 
    QuestionModel, 
    MemberModel, 
    BookModel, 
    getPageNumberFromId,
    KOREAN_CATEGORIES,
    READING_BRANCHES,
    LITERATURE_BRANCHES,
    SPEECH_WRITING_BRANCHES,
    LANGUAGE_MEDIA_BRANCHES,
    KoreanCategory,
    KoreanBranch,
    KoreanGenre
} from '../../../../models';
import {
    getParagraphDocId
} from '../../../../services/koreanAnalysisService';
import EditUnitRoot from './EditUnitRoot';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { SortableRow } from './SortableRow';
import { SortableSidebarItem } from './SortableSidebarItem';
import {
    Trash2, X, Edit2, Plus, Check, Loader2, FolderPlus, Layers, FileText, Sparkles
} from 'lucide-react';
import PdfSheetPreviewModal from './PdfSheetPreviewModal';

interface SetDashboardProps {
    bookId: string;
    branchId?: string;
}

const SetDashboard: React.FC<SetDashboardProps> = ({ bookId, branchId }) => {
    // 1. States
    const [chapters, setChapters] = useState<BookSetModel[]>([]);
    const [paragraphs, setParagraphs] = useState<KoreanTextModel[]>([]);
    const [selectedSetId, setSelectedSetId] = useState<string>('all_sets');
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [isSaving, setIsSaving] = useState<string | null>(null);
    const [activeBook, setActiveBook] = useState<BookModel | null>(null);
    const isKorean = activeBook ? activeBook.subject === '국어' : true;
    
    // 세트 추가용 상태
    const [isNewSetModalOpen, setIsNewSetModalOpen] = useState(false);
    const [newSetTitle, setNewSetTitle] = useState('');
    
    // 세트 이름 수정용 상태
    const [isRenamingSet, setIsRenamingSet] = useState(false);
    const [renamingTitle, setRenamingTitle] = useState('');
    
    // 세트 이름 수정용 상태 (사이드바)
    const [editingSetId, setEditingSetId] = useState<string | null>(null);
    const [editingSetTitle, setEditingSetTitle] = useState('');
    
    // 페이지 추가용 상태
    const [isNewPageModalOpen, setIsNewPageModalOpen] = useState(false);
    const [newPageNumber, setNewPageNumber] = useState<number>(1);
    const [newPageTitle, setNewPageTitle] = useState('');

    // 페이지 상세 편집 화면 제어 상태
    const [activeEditingUnit, setActiveEditingUnit] = useState<KoreanTextModel | null>(null);

    // PDF 시트 인쇄 미리보기 제어 상태
    const [isPdfPreviewOpen, setIsPdfPreviewOpen] = useState(false);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewConceptDatas, setPreviewConceptDatas] = useState<KoreanTextModel[]>([]);
    const [previewQuestionSets, setPreviewQuestionSets] = useState<{ [paragraphId: string]: any }>({});

    // 2. Dnd-kit Sensors
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // 3. Realtime Load
    useEffect(() => {
        if (!bookId) return;

        // 교재 정보 로드
        getDoc(doc(db, COLLECTIONS.BOOKS, bookId)).then(snap => {
            if (snap.exists()) setActiveBook(snap.data() as BookModel);
        });

        // 챕터/세트 로드
        const qChapters = query(collection(db, COLLECTIONS.BOOK_CHAPTER_SET), where('bookId', '==', bookId));
        const unsubChapters = onSnapshot(qChapters, (snap) => {
            const list: BookSetModel[] = [];
            const batch = writeBatch(db);
            let hasLegacy = false;

            snap.forEach(docSnap => {
                const data = docSnap.data();
                const uIds = data.unitIds || data.koreanTextIds || [];

                list.push({
                    id: docSnap.id,
                    ...data,
                    unitIds: uIds
                } as BookSetModel);

                const updates: any = {};
                let docNeedUpdate = false;

                // 1) 만약 레거시 paragraphIds 필드가 남아있다면 정리
                if ('paragraphIds' in data) {
                    updates.paragraphIds = deleteField();
                    docNeedUpdate = true;
                }

                // 2) 만약 레거시 koreanTextIds 필드가 남아있다면 unitIds로 이전하고 정리
                if ('koreanTextIds' in data) {
                    updates.unitIds = uIds;
                    updates.koreanTextIds = deleteField();
                    docNeedUpdate = true;
                }

                if (docNeedUpdate) {
                    batch.update(doc(db, COLLECTIONS.BOOK_CHAPTER_SET, docSnap.id), updates);
                    hasLegacy = true;
                }
            });

            if (hasLegacy) {
                batch.commit().catch(err => console.error('Failed to clean up legacy fields:', err));
            }
            list.sort((a, b) => {
                const orderA = a.order !== undefined ? a.order : 9999;
                const orderB = b.order !== undefined ? b.order : 9999;
                if (orderA !== orderB) return orderA - orderB;
                return a.updatedAt - b.updatedAt;
            });
            setChapters(list);
        });

        // 국어 지문/페이지 로드
        const qParas = query(collection(db, COLLECTIONS.BOOK_UNIT), where('bookId', '==', bookId));
        const unsubParas = onSnapshot(qParas, (snap) => {
            const list: KoreanTextModel[] = [];
            snap.forEach(doc => list.push({ id: doc.id, ...doc.data() } as KoreanTextModel));
            setParagraphs(list);
        });

        return () => {
            unsubChapters();
            unsubParas();
        };
    }, [bookId]);

    // 4. Computed List
    const activeSet = useMemo(() => {
        return chapters.find(c => c.id === selectedSetId);
    }, [chapters, selectedSetId]);

    const sortedParagraphs = useMemo(() => {
        if (selectedSetId === 'all_sets') {
            return [...paragraphs].sort((a, b) => getPageNumberFromId(a.id) - getPageNumberFromId(b.id));
        }
        if (!activeSet) return [];
        
        // 세트 내의 unitIds 순서대로 나열
        const mapped = activeSet.unitIds
            .map(id => paragraphs.find(p => p.id === id))
            .filter(Boolean) as KoreanTextModel[];
            
        // 세트에 배정되었으나 unitIds 배열에 누락된 항목들 병합
        const missing = paragraphs.filter(p => p.setId === selectedSetId && !activeSet.unitIds.includes(p.id));
        return [...mapped, ...missing];
    }, [paragraphs, activeSet, selectedSetId]);

    // 5. Set (Chapter) Actions
    const handleCreateSet = async () => {
        if (!newSetTitle.trim()) return alert('단원명을 입력해주세요.');
        
        try {
            const setRef = doc(collection(db, COLLECTIONS.BOOK_CHAPTER_SET));
            const newSet: BookSetModel = {
                id: setRef.id,
                bookId,
                title: newSetTitle.trim(),
                unitIds: [],
                updatedAt: Date.now(),
                order: chapters.length
            };
            await setDoc(setRef, newSet);
            setSelectedSetId(setRef.id);
            setNewSetTitle('');
            setIsNewSetModalOpen(false);
        } catch (err: any) {
            alert('세트 생성 실패: ' + err.message);
        }
    };

    const handleDeleteSet = async (setId: string) => {
        if (!confirm('정말로 이 단원(세트)을 삭제하시겠습니까?\n단원 내 지문들은 삭제되지 않고 해제됩니다.')) return;
        
        try {
            const batch = writeBatch(db);
            const assignedParas = paragraphs.filter(p => p.setId === setId);
            assignedParas.forEach(p => {
                const ref = doc(db, COLLECTIONS.BOOK_UNIT, p.id);
                batch.update(ref, { setId: '' });
            });
            batch.delete(doc(db, COLLECTIONS.BOOK_CHAPTER_SET, setId));
            await batch.commit();
            setSelectedSetId('all_sets');
        } catch (err: any) {
            alert('세트 삭제 실패: ' + err.message);
        }
    };

    const handleRenameSet = async () => {
        if (!selectedSetId || selectedSetId === 'all_sets') return;
        const trimmed = renamingTitle.trim();
        if (!trimmed) {
            alert('단원 이름을 입력해 주세요.');
            return;
        }
        try {
            const docRef = doc(db, COLLECTIONS.BOOK_CHAPTER_SET, selectedSetId);
            await updateDoc(docRef, {
                title: trimmed,
                updatedAt: Date.now()
            });
            setIsRenamingSet(false);
        } catch (err: any) {
            alert('단원 이름 수정 실패: ' + err.message);
        }
    };

    const handleRenameSidebarSet = async (setId: string) => {
        const trimmed = editingSetTitle.trim();
        if (!trimmed) {
            setEditingSetId(null);
            return;
        }
        try {
            const docRef = doc(db, COLLECTIONS.BOOK_CHAPTER_SET, setId);
            await updateDoc(docRef, {
                title: trimmed,
                updatedAt: Date.now()
            });
            setEditingSetId(null);
        } catch (err: any) {
            alert('단원 이름 수정 실패: ' + err.message);
        }
    };

    useEffect(() => {
        setIsRenamingSet(false);
        setEditingSetId(null);
    }, [selectedSetId]);

    // 6. Page / Paragraph Actions
    const handleCreatePage = async () => {
        const nextNumber = paragraphs.length > 0 
            ? Math.max(...paragraphs.map(p => getPageNumberFromId(p.id))) + 1 
            : 1;
        
        const docId = getParagraphDocId(bookId, nextNumber);
        const docRef = doc(db, COLLECTIONS.BOOK_UNIT, docId);
        
        try {
            const snap = await getDoc(docRef);
            if (snap.exists()) return alert('이미 지문 분석 카드가 존재합니다.');

            const newPage: KoreanTextModel = {
                id: docId,
                bookId,
                setId: selectedSetId !== 'all_sets' ? selectedSetId : '',
                title: newPageTitle.trim() || `지문 카드 ${nextNumber}`,
                updatedAt: Date.now(),
                _stats: {
                    conceptDone: false,
                    analysisDone: false,
                    questionsCount: 0,
                    updatedAt: Date.now()
                }
            };
            
            await setDoc(docRef, newPage);
            
            if (selectedSetId !== 'all_sets') {
                await updateDoc(doc(db, COLLECTIONS.BOOK_CHAPTER_SET, selectedSetId), {
                    unitIds: arrayUnion(docId)
                });
            }

            setNewPageNumber(newPageNumber + 1);
            setNewPageTitle('');
            setIsNewPageModalOpen(false);
        } catch (err: any) {
            alert('페이지 추가 실패: ' + err.message);
        }
    };

    const handleDeletePage = async (p: KoreanTextModel) => {
        if (!confirm(`정말로 ${getPageNumberFromId(p.id)}페이지의 분석 내용 및 생성된 OMR 문항을 삭제하시겠습니까?`)) return;
        
        try {
            const batch = writeBatch(db);
            if (p.questionSetIds && p.questionSetIds.length > 0) {
                for (const setId of p.questionSetIds) {
                    const setSnap = await getDoc(doc(db, COLLECTIONS.BOOK_QUESTION_SETS, setId));
                    if (setSnap.exists()) {
                        const setData = setSnap.data();
                        const qIds = setData?.questionIds || [];
                        qIds.forEach((qId: string) => {
                            batch.delete(doc(db, COLLECTIONS.BOOK_QUESTIONS, qId));
                        });
                    }
                    batch.delete(doc(db, COLLECTIONS.BOOK_QUESTION_SETS, setId));
                }
            }
            if (p.setId) {
                const setRef = doc(db, COLLECTIONS.BOOK_CHAPTER_SET, p.setId);
                batch.update(setRef, {
                    unitIds: arrayRemove(p.id)
                });
            }
            batch.delete(doc(db, COLLECTIONS.BOOK_UNIT, p.id));
            await batch.commit();
        } catch (err: any) {
            alert('삭제 실패: ' + err.message);
        }
    };

    // 7. Drag & Drop Reorder
    const handleDragEnd = async (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id || !activeSet) return;

        const currentIds = sortedParagraphs.map(p => p.id);
        const oldIndex = currentIds.indexOf(active.id as string);
        const newIndex = currentIds.indexOf(over.id as string);
        
        if (oldIndex === -1 || newIndex === -1) return;

        const newOrder = arrayMove(currentIds, oldIndex, newIndex);
        
        try {
            await updateDoc(doc(db, COLLECTIONS.BOOK_CHAPTER_SET, activeSet.id), {
                unitIds: newOrder,
                updatedAt: Date.now()
            });
        } catch (err: any) {
            console.error("Dnd save error:", err);
        }
    };

    const handleDragEndChapters = async (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIndex = chapters.findIndex(c => c.id === active.id);
        const newIndex = chapters.findIndex(c => c.id === over.id);
        if (oldIndex === -1 || newIndex === -1) return;

        const reordered = arrayMove(chapters, oldIndex, newIndex);
        setChapters(reordered);

        try {
            const batch = writeBatch(db);
            reordered.forEach((chapter, idx) => {
                const docRef = doc(db, COLLECTIONS.BOOK_CHAPTER_SET, chapter.id);
                batch.update(docRef, { order: idx });
            });
            await batch.commit();
        } catch (err: any) {
            alert('단원 순서 변경 실패: ' + err.message);
        }
    };

    // 8. Bulk Allocation / Actions
    const toggleSelect = (id: string) => {
        setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const toggleSelectAll = () => {
        if (selectedIds.length === sortedParagraphs.length) {
            setSelectedIds([]);
        } else {
            setSelectedIds(sortedParagraphs.map(p => p.id));
        }
    };

    const handleBulkAssignSet = async (targetSetId: string) => {
        if (selectedIds.length === 0) return alert('선택된 항목이 없습니다.');
        
        try {
            const batch = writeBatch(db);
            
            chapters.forEach(set => {
                const filtered = set.unitIds.filter(id => !selectedIds.includes(id));
                if (filtered.length !== set.unitIds.length) {
                    batch.update(doc(db, COLLECTIONS.BOOK_CHAPTER_SET, set.id), {
                        unitIds: filtered
                    });
                }
            });

            if (targetSetId !== 'all_sets') {
                const targetSet = chapters.find(c => c.id === targetSetId);
                if (targetSet) {
                    const mergedIds = [...new Set([...targetSet.unitIds, ...selectedIds])];
                    batch.update(doc(db, COLLECTIONS.BOOK_CHAPTER_SET, targetSetId), {
                        unitIds: mergedIds
                    });
                }
                
                selectedIds.forEach(id => {
                    batch.update(doc(db, COLLECTIONS.BOOK_UNIT, id), { setId: targetSetId });
                });
            } else {
                selectedIds.forEach(id => {
                    batch.update(doc(db, COLLECTIONS.BOOK_UNIT, id), { setId: '' });
                });
            }

            await batch.commit();
            setSelectedIds([]);
            alert('단원 배정이 완료되었습니다.');
        } catch (err: any) {
            alert('일괄 배정 오류: ' + err.message);
        }
    };

    // PDF 시트 보기 핸들러
    const handleOpenPdfPreviewForSelected = async () => {
        if (selectedIds.length === 0) return;
        setPreviewLoading(true);
        setIsPdfPreviewOpen(true);
        try {
            // Find selected paragraphs data
            const selectedParagraphs = paragraphs.filter(p => selectedIds.includes(p.id));
            // Sort by order or page number to keep them sequential
            selectedParagraphs.sort((a, b) => {
                const aNum = parseInt(a.id.split('_page_')[1]) || 0;
                const bNum = parseInt(b.id.split('_page_')[1]) || 0;
                return aNum - bNum;
            });
            setPreviewConceptDatas(selectedParagraphs);

            const qSetsMap: { [paragraphId: string]: any } = {};

            // Load questions for each selected paragraph
            for (const p of selectedParagraphs) {
                // Fetch the latest question sets for this paragraph
                const qSetsQuery = query(
                    collection(db, COLLECTIONS.BOOK_QUESTION_SETS),
                    where('paragraphId', '==', p.id)
                );
                const qSetsSnap = await getDocs(qSetsQuery);
                if (!qSetsSnap.empty) {
                    // Get all sets, sorted chronologically (oldest first)
                    const sets = qSetsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() } as any));
                    sets.sort((a, b) => a.createdAt - b.createdAt);

                    const setsWithQuestions = await Promise.all(sets.map(async s => {
                        const qQuery = query(
                            collection(db, COLLECTIONS.BOOK_QUESTIONS),
                            where('setId', '==', s.id)
                        );
                        const qSnap = await getDocs(qQuery);
                        const questions = qSnap.docs.map(doc => ({ id: doc.id, ...doc.data() } as any));
                        questions.sort((a, b) => (a.number || 0) - (b.number || 0));
                        return {
                            ...s,
                            questions: questions
                        };
                    }));

                    qSetsMap[p.id] = setsWithQuestions;
                }
            }

            setPreviewQuestionSets(qSetsMap);
        } catch (err) {
            console.error('Failed to load PDF preview data:', err);
        } finally {
            setPreviewLoading(false);
        }
    };

    if (activeEditingUnit) {
        return (
            <EditUnitRoot
                unit={activeEditingUnit}
                bookId={bookId}
                activeBook={activeBook!}
                onBack={() => {
                    setActiveEditingUnit(null);
                }}
            />
        );
    }

    return (
        <div className="flex h-[calc(100vh-120px)] bg-slate-50 overflow-hidden relative w-full border-t border-slate-200">
            {/* 1. Left Chapter (Set) Sidebar Panel */}
            <div className="w-60 border-r border-slate-200 flex flex-col h-full bg-white shrink-0">
                <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
                    <span className="font-black text-xs text-slate-700 uppercase tracking-wider">교재 단원(세트)</span>
                    <button
                        onClick={() => setIsNewSetModalOpen(true)}
                        className="p-1.5 hover:bg-slate-200 rounded-lg text-slate-500 hover:text-slate-800 transition-colors"
                        title="새 단원 추가"
                    >
                        <FolderPlus size={15} />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-2 space-y-1 custom-scrollbar">
                    <button
                        onClick={() => setSelectedSetId('all_sets')}
                        className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-left text-xs font-black transition-all ${
                            selectedSetId === 'all_sets'
                                ? 'bg-indigo-600 text-white shadow-md'
                                : 'text-slate-600 hover:bg-slate-100'
                        }`}
                    >
                        <Layers size={14} />
                        <span>전체 목록 보기</span>
                    </button>

                    <div className="border-t border-slate-100 my-2" />

                    {chapters.length === 0 ? (
                        <div className="p-4 text-center text-[10px] text-slate-400 font-bold leading-normal">
                            추가된 단원이 없습니다.<br />위의 아이콘을 눌러 단원을 생성하세요.
                        </div>
                    ) : (
                        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEndChapters}>
                            <SortableContext items={chapters.map(c => c.id)} strategy={verticalListSortingStrategy}>
                                <div className="space-y-1">
                                    {chapters.map(chapter => {
                                        const isEditingThis = editingSetId === chapter.id;
                                        return (
                                            <SortableSidebarItem key={chapter.id} id={chapter.id}>
                                                {isEditingThis ? (
                                                    <div
                                                        className="w-full flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white border border-indigo-300"
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        <input
                                                            type="text"
                                                            value={editingSetTitle}
                                                            onChange={(e) => setEditingSetTitle(e.target.value)}
                                                            className="flex-1 min-w-0 bg-transparent text-xs font-bold text-slate-700 focus:outline-none"
                                                            autoFocus
                                                            onKeyDown={async (e) => {
                                                                if (e.key === 'Enter') {
                                                                    await handleRenameSidebarSet(chapter.id);
                                                                } else if (e.key === 'Escape') {
                                                                    setEditingSetId(null);
                                                                }
                                                            }}
                                                            onBlur={async () => {
                                                                await handleRenameSidebarSet(chapter.id);
                                                            }}
                                                        />
                                                    </div>
                                                ) : (
                                                    <button
                                                        onClick={() => setSelectedSetId(chapter.id)}
                                                        className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-left text-xs font-bold transition-all group ${
                                                            selectedSetId === chapter.id
                                                                ? 'bg-indigo-50 text-indigo-600 border-l-4 border-indigo-600 pl-2'
                                                                : 'text-slate-600 hover:bg-slate-50 border-l-4 border-transparent'
                                                        }`}
                                                    >
                                                        <span className="truncate pr-2">{chapter.title}</span>
                                                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                            <span
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setEditingSetTitle(chapter.title);
                                                                    setEditingSetId(chapter.id);
                                                                }}
                                                                className="p-1 hover:bg-indigo-50 text-slate-400 hover:text-indigo-600 rounded transition-all"
                                                                title="단원 수정"
                                                            >
                                                                <Edit2 size={12} />
                                                            </span>
                                                            <span
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleDeleteSet(chapter.id);
                                                                }}
                                                                className="p-1 hover:bg-rose-50 text-slate-400 hover:text-rose-500 rounded transition-all"
                                                                title="단원 삭제"
                                                            >
                                                                <Trash2 size={12} />
                                                            </span>
                                                        </div>
                                                    </button>
                                                )}
                                            </SortableSidebarItem>
                                        );
                                    })}
                                </div>
                            </SortableContext>
                        </DndContext>
                    )}
                </div>
            </div>

            {/* 2. Right Wide Workspace Panel (Grid Table) */}
            <div className="flex-1 flex flex-col h-full overflow-hidden bg-slate-50">
                {/* Top Toolbar */}
                <div className="bg-white border-b border-slate-200 px-6 py-4 flex flex-wrap items-center justify-between gap-4 shrink-0">
                    <div className="flex items-center gap-3">
                        {isRenamingSet ? (
                            <div className="flex items-center gap-2">
                                <input
                                    type="text"
                                    value={renamingTitle}
                                    onChange={(e) => setRenamingTitle(e.target.value)}
                                    className="px-2 py-1 text-xs border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 font-bold text-slate-700 bg-white"
                                    autoFocus
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') handleRenameSet();
                                        if (e.key === 'Escape') setIsRenamingSet(false);
                                    }}
                                />
                                <button
                                    onClick={handleRenameSet}
                                    className="px-2 py-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-[10px] font-black transition-colors"
                                >
                                    저장
                                </button>
                                <button
                                    onClick={() => setIsRenamingSet(false)}
                                    className="px-2 py-1 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-lg text-[10px] font-black transition-colors"
                                >
                                    취소
                                </button>
                            </div>
                        ) : (
                            <div className="flex items-center gap-2">
                                <span className="font-black text-sm text-slate-800">
                                    {selectedSetId === 'all_sets'
                                        ? '전체 교재 목록'
                                        : `${chapters.find(c => c.id === selectedSetId)?.title || '단원'}`
                                    }
                                </span>
                                {selectedSetId !== 'all_sets' && (
                                    <button
                                        onClick={() => {
                                            const currentTitle = chapters.find(c => c.id === selectedSetId)?.title || '';
                                            setRenamingTitle(currentTitle);
                                            setIsRenamingSet(true);
                                        }}
                                        className="p-1 hover:bg-slate-100 rounded text-slate-400 hover:text-indigo-600 transition-colors"
                                        title="단원 이름 수정"
                                    >
                                        <Edit2 size={12} />
                                    </button>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="flex items-center gap-2">
                        {/* 세트 일괄 지정 */}
                        {selectedIds.length > 0 && (
                            <div className="flex items-center gap-1.5 border-r border-slate-200 pr-3 mr-1">
                                <span className="text-[11px] font-black text-indigo-600 bg-indigo-50 px-2 py-1 rounded-lg">{selectedIds.length}개 선택됨</span>
                                <select
                                    onChange={(e) => handleBulkAssignSet(e.target.value)}
                                    defaultValue=""
                                    className="px-2 py-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-lg text-xs font-bold focus:outline-none transition-all cursor-pointer border border-indigo-100"
                                >
                                    <option value="" disabled>단원 일괄 배정...</option>
                                    <option value="all_sets">단원 지정 해제</option>
                                    {chapters.map(c => (
                                        <option key={c.id} value={c.id}>{c.title}</option>
                                    ))}
                                </select>
                                <button
                                    onClick={handleOpenPdfPreviewForSelected}
                                    className="flex items-center gap-1.5 px-3 py-1.5 bg-rose-600 hover:bg-rose-700 text-white rounded-lg text-xs font-black shadow-sm transition-all hover:scale-105 active:scale-95 border border-rose-600 cursor-pointer"
                                >
                                    <FileText size={13} />
                                    <span>PDF 시트보기</span>
                                </button>
                            </div>
                        )}

                        <button
                            onClick={() => {
                                setNewPageTitle('');
                                setIsNewPageModalOpen(true);
                            }}
                            className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-black shadow transition-all"
                        >
                            <Plus size={14} />
                            <span>새 학습 페이지 추가</span>
                        </button>
                    </div>
                </div>

                {/* List & Table area / PDF Study area */}
                <div className="flex-1 overflow-auto bg-white flex flex-col">
                    <div className="w-full flex-1">
                        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                                <SortableContext items={sortedParagraphs.map(p => p.id)} strategy={verticalListSortingStrategy}>
                                    <table className="w-full text-left border-collapse table-fixed">
                                        <thead>
                                            <tr className="bg-slate-50/70 border-b border-slate-200 text-slate-500 font-bold text-[10px] uppercase tracking-wider">
                                                <th className="py-3 px-1 w-8 text-center">정렬</th>
                                                <th className="py-3 px-2 w-10 text-center">
                                                    <input 
                                                        type="checkbox" 
                                                        checked={selectedIds.length === sortedParagraphs.length && sortedParagraphs.length > 0} 
                                                        onChange={toggleSelectAll}
                                                        className="w-4 h-4 cursor-pointer rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" 
                                                    />
                                                </th>
                                                <th className="py-3 px-3 w-32">단원(세트)</th>
                                                <th className="py-3 px-3 w-48">학습 페이지 제목</th>
                                                <th className="py-3 px-3 w-[80px] text-center">개념설명</th>
                                                {isKorean && <th className="py-3 px-3 w-[80px] text-center">단락분석</th>}
                                                <th className="py-3 px-3 w-[80px] text-center">실전문항</th>
                                                <th className="py-3 px-3 w-12 text-center">관리</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {sortedParagraphs.length === 0 ? (
                                                <tr>
                                                    <td colSpan={isKorean ? 8 : 7} className="py-12 text-center text-slate-400 font-bold text-xs">
                                                        등록된 학습 페이지가 없습니다. 우측 상단의 '새 학습 페이지 추가'를 눌러 시작하세요.
                                                    </td>
                                                </tr>
                                            ) : (
                                                sortedParagraphs.map(p => {
                                                    const isCollapsed = selectedSetId === 'all_sets';
                                                    return (
                                                        <SortableRow key={p.id} id={p.id} readOnly={isCollapsed}>
                                                            <td className="py-2.5 px-2 text-center">
                                                                <input 
                                                                    type="checkbox" 
                                                                    checked={selectedIds.includes(p.id)} 
                                                                    onChange={() => toggleSelect(p.id)}
                                                                    className="w-4 h-4 cursor-pointer rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" 
                                                                />
                                                            </td>
                                                            <td className="py-2.5 px-3 font-semibold text-xs text-slate-500 truncate">
                                                                {chapters.find(c => c.id === p.setId)?.title || '-'}
                                                            </td>
                                                            <td className="py-2.5 px-3 font-black text-xs text-slate-700 truncate cursor-pointer hover:text-indigo-600 flex items-center gap-1.5 group"
                                                                onClick={() => setActiveEditingUnit(p)}
                                                            >
                                                                <Edit2 size={10} className="text-slate-400 group-hover:text-indigo-600" />
                                                                <span className="truncate">{p.title}</span>
                                                            </td>
                                                            
                                                            {/* 개념설명 태스크 셀 */}
                                                            <td className="py-2.5 px-3 text-center">
                                                                {p._stats?.conceptDone || (p.concepts && p.concepts.length > 0) ? (
                                                                    <button 
                                                                        onClick={() => setActiveEditingUnit(p)}
                                                                        className="px-2 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg text-[9px] font-black hover:bg-emerald-100 transition-colors flex items-center justify-center gap-1 mx-auto"
                                                                    >
                                                                        <Check size={9} strokeWidth={4} />
                                                                        <span>{p.concepts?.length || 0}개 카드</span>
                                                                    </button>
                                                                ) : (
                                                                    <button 
                                                                        onClick={() => setActiveEditingUnit(p)}
                                                                        className="px-2 py-1 bg-slate-100 text-slate-500 border border-slate-200 rounded-lg text-[9px] font-black hover:bg-slate-200 hover:text-indigo-600 transition-colors flex items-center justify-center gap-1 mx-auto"
                                                                    >
                                                                        <span>미작성</span>
                                                                    </button>
                                                                )}
                                                            </td>

                                                            {/* 단락분석 태스크 셀 */}
                                                            {isKorean && (
                                                                <td className="py-2.5 px-3 text-center">
                                                                    {p._stats?.analysisDone || p.analysisNote ? (
                                                                        <button 
                                                                            onClick={() => setActiveEditingUnit(p)}
                                                                            className="px-2 py-1 bg-violet-50 text-violet-700 border border-violet-200 rounded-lg text-[9px] font-black hover:bg-violet-100 transition-colors flex items-center justify-center gap-1 mx-auto"
                                                                        >
                                                                            <Check size={9} strokeWidth={4} />
                                                                            <span>작성완료</span>
                                                                        </button>
                                                                    ) : (
                                                                        <button 
                                                                            onClick={() => setActiveEditingUnit(p)}
                                                                            className="px-2 py-1 bg-slate-100 text-slate-500 border border-slate-200 rounded-lg text-[9px] font-black hover:bg-slate-200 hover:text-indigo-600 transition-colors flex items-center justify-center gap-1 mx-auto"
                                                                        >
                                                                            <span>미작성</span>
                                                                        </button>
                                                                    )}
                                                                </td>
                                                            )}

                                                            {/* 실전문항 태스크 셀 */}
                                                            <td className="py-2.5 px-3 text-center">
                                                                {(p.questionSetIds && p.questionSetIds.length > 0) ? (
                                                                    <button
                                                                        onClick={() => setActiveEditingUnit(p)}
                                                                        className="px-2 py-1 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-lg text-[9px] font-black hover:bg-indigo-100 transition-all mx-auto"
                                                                    >
                                                                        {`${p.questionSetIds.length}개 세트`}
                                                                    </button>
                                                                ) : (
                                                                    <button 
                                                                        onClick={() => setActiveEditingUnit(p)}
                                                                        className="px-2 py-1 bg-slate-100 text-slate-500 border border-slate-200 rounded-lg text-[9px] font-black hover:bg-slate-200 hover:text-indigo-600 transition-colors flex items-center justify-center gap-1 mx-auto"
                                                                    >
                                                                        <span>미출제</span>
                                                                    </button>
                                                                )}
                                                            </td>

                                                            <td className="py-2.5 px-3 text-center">
                                                                <button
                                                                    onClick={() => handleDeletePage(p)}
                                                                    className="p-1.5 text-slate-400 hover:text-rose-500 rounded-lg hover:bg-rose-50 transition-colors mx-auto"
                                                                    title="지문 카드 삭제"
                                                                >
                                                                    <Trash2 size={13} />
                                                                </button>
                                                            </td>
                                                    </SortableRow>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </SortableContext>
                        </DndContext>
                    </div>
                </div>
            </div>

            {/* 1. 새 단원 생성 모달 */}
            {isNewSetModalOpen && (
                <div className="fixed inset-0 z-[9999] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl border border-slate-200/80 animate-in fade-in zoom-in-95 duration-200">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-black text-sm text-slate-800">새 단원(세트) 생성</h3>
                            <button onClick={() => setIsNewSetModalOpen(false)} className="p-1.5 text-slate-400 hover:bg-slate-50 rounded-lg"><X size={16} /></button>
                        </div>
                        <input
                            type="text"
                            placeholder="예: 01강 - 현대시의 기법"
                            value={newSetTitle}
                            onChange={(e) => setNewSetTitle(e.target.value)}
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-semibold text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500 text-slate-700 mb-6"
                        />
                        <div className="flex justify-end gap-2">
                            <button onClick={() => setIsNewSetModalOpen(false)} className="px-4 py-2 text-slate-500 hover:bg-slate-50 rounded-xl font-bold text-xs transition-all">취소</button>
                            <button onClick={handleCreateSet} className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-black text-xs transition-all shadow-md">단원 만들기</button>
                        </div>
                    </div>
                </div>
            )}

            {/* 2. 새 페이지 추가 모달 */}
            {isNewPageModalOpen && (
                <div className="fixed inset-0 z-[9999] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl border border-slate-200/80 animate-in fade-in zoom-in-95 duration-200">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-black text-sm text-slate-800">새 학습 페이지 생성</h3>
                            <button onClick={() => setIsNewPageModalOpen(false)} className="p-1.5 text-slate-400 hover:bg-slate-50 rounded-lg"><X size={16} /></button>
                        </div>
                        <div className="space-y-4 mb-6">
                            <div>
                                <label className="block text-[10px] font-black text-slate-500 uppercase mb-1.5">학습 페이지 제목</label>
                                <input
                                    type="text"
                                    placeholder="예: 산수유나무의 농사, 01단원 물리학 개념설명 등"
                                    value={newPageTitle}
                                    onChange={(e) => setNewPageTitle(e.target.value)}
                                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-semibold text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500 text-slate-700"
                                />
                            </div>
                        </div>
                        <div className="flex justify-end gap-2">
                            <button onClick={() => setIsNewPageModalOpen(false)} className="px-4 py-2 text-slate-500 hover:bg-slate-50 rounded-xl font-bold text-xs transition-all">취소</button>
                            <button onClick={handleCreatePage} className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-black text-xs transition-all shadow-md">생성</button>
                        </div>
                    </div>
                </div>
            )}
            {/* PDF 시트 인쇄 미리보기 모달 */}
            <PdfSheetPreviewModal
                isOpen={isPdfPreviewOpen}
                onClose={() => setIsPdfPreviewOpen(false)}
                selectedParagraphs={previewConceptDatas}
                questionSets={previewQuestionSets}
                bookTitle={activeBook?.title || '교재'}
                subject={activeBook?.subject}
            />

            {previewLoading && (
                <div className="fixed inset-0 z-[10000] bg-slate-950/80 backdrop-blur-sm flex flex-col items-center justify-center text-slate-100 gap-4 select-none animate-in fade-in duration-200">
                    <div className="relative w-16 h-16 flex items-center justify-center">
                        <Loader2 size={40} className="animate-spin text-indigo-500" />
                        <Sparkles size={16} className="absolute text-violet-400 animate-pulse" />
                    </div>
                    <div className="flex flex-col items-center gap-1.5">
                        <span className="text-sm font-black tracking-wide text-slate-200">인쇄용 문항 시트를 빌드하고 있습니다</span>
                        <span className="text-xs text-slate-400 font-bold">잠시만 기다려 주세요...</span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SetDashboard;
